import { getOrderRows } from './pdfExport';
import type { OrderData, EventData } from './pdfExport';
import { currencySymbol } from '@/utils/currency';

/**
 * Escapes a CSV field value
 */
function escapeCSVField(value: any): string {
    if (value === null || value === undefined) {
        return '';
    }
    const stringValue = String(value);
    // If the value contains comma, quote, or newline, wrap it in quotes and escape internal quotes
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
}

/**
 * Converts orders data to CSV format matching PDF export structure
 * @param orders - Array of order data
 * @param eventData - Event information
 * @returns CSV string
 */
export function exportOrdersToCSV(orders: OrderData[], eventData: EventData): string {
    // Build CSV rows
    const rows: string[] = [];

    // Main Orders Table - matching PDF export structure
    const ordersHeaders = ['#', 'Name', 'Email', 'Phone', 'Order ID', 'Date', 'Tickets', 'Amount', 'Status'];
    rows.push(ordersHeaders.map(escapeCSVField).join(','));

    // Add orders data matching PDF structure
    orders.forEach((order, index) => {
        const orderDate = new Date(order.createdAt).toLocaleDateString();
        getOrderRows(order, 'N/A').forEach(orderRow => {
            const row = [
                index + 1,
                orderRow.name,
                orderRow.email,
                orderRow.phone,
                order.hashedUniqueId || order.orderId,
                orderDate,
                orderRow.tickets,
                orderRow.showAmount ? `${currencySymbol(order.currency || eventData.currency)}${order.totalAmount.toFixed(2)}` : '',
                order.status
            ];
            rows.push(row.map(escapeCSVField).join(','));
        });
    });

    return rows.join('\n');
}

/**
 * Downloads orders data as CSV file
 * @param orders - Array of order data
 * @param eventData - Event information
 * @param filename - Optional custom filename
 */
export function downloadOrdersAsCSV(
    orders: OrderData[],
    eventData: EventData,
    filename?: string
): void {
    try {
        const csvContent = exportOrdersToCSV(orders, eventData);
        const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const csvUrl = URL.createObjectURL(csvBlob);
        const link = document.createElement('a');
        link.href = csvUrl;
        
        // Generate filename
        const eventTitle = eventData.title.replace(/[^a-z0-9]/gi, '_').toLowerCase();
        const dateStr = new Date().toISOString().split('T')[0];
        link.download = filename || `orders_${eventTitle}_${dateStr}.csv`;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(csvUrl);
    } catch (error) {
        console.error('Error exporting orders to CSV:', error);
        throw new Error('Failed to export orders to CSV');
    }
}
